<template>
  <div class="editor-product-routes" aria-label="按产品配置目标页面">
    <p class="editor-navigation-hint">一条规则可选择多个产品，共用一个目标页面；不同布局另加规则。{{ kind === 'device' ? '按点击设备所属的产品匹配目标设备页面，同时传入实际 productId 和 deviceId，不继承旧设备。' : '跳转时自动传入实际点击的产品编号，并清除旧设备参数。' }}</p>
    <p v-if="loading" class="editor-navigation-hint" role="status">正在读取可选择的产品…</p>
    <div v-if="loadError" class="editor-navigation-error" role="alert">
      {{ loadError }}
      <el-button size="mini" type="text" @click="loadProducts">重新加载产品</el-button>
    </div>
    <p v-if="!loading && !loadError && !choices.length" class="editor-navigation-hint" role="status">{{ platform ? '暂无可选择的产品，请检查产品访问权限。' : kind === 'device' ? '暂无静态设备的所属产品，请先在数据页签配置设备列表。' : '暂无静态产品，请先在数据页签配置产品列表。' }}</p>
    <p v-if="!routes.length" class="editor-navigation-hint">尚未添加产品规则；列表使用下方的未配置产品动作。</p>
    <fieldset v-for="(route, index) in routes" :key="index" class="editor-product-routes__rule">
      <legend>产品规则 {{ index + 1 }}</legend>
      <el-form-item label="产品（多选）" :for="'product-route-' + index">
        <el-select :id="'product-route-picker-' + index" :ref="'product-' + index" :value="route.productIds" multiple collapse-tags filterable clearable :reserve-keyword="false" :loading="loading"
                   v-product-route-label="{ id: 'product-route-' + index, label: '规则' + (index + 1) + '产品（可多选）' }"
                   :aria-describedby="'product-route-help-' + index" placeholder="搜索产品名称或编号，可多选" no-match-text="没有匹配的产品，请更换名称或编号" @input="updateRoute(index, 'productIds', $event)">
          <el-option v-for="id in missingProducts(route)" :key="id" :value="id" :label="id + '（未加载或不可访问，可移除）'"></el-option>
          <el-option v-for="item in choices" :key="item.id" :value="item.id" :label="item.name + ' · ' + item.id + (productDisabled(item.id, index) ? '（已分配到其他规则）' : '')" :disabled="productDisabled(item.id, index)"></el-option>
        </el-select>
        <p :id="'product-route-help-' + index" class="editor-navigation-hint" role="status">已选择 {{ route.productIds.length }} 个产品，共用下方目标页面。可在下拉中取消选择。</p>
        <div class="editor-product-routes__selection-actions">
          <el-button size="mini" type="text" :disabled="loading || !choices.length" :aria-label="'规则' + (index + 1) + '全选未分配产品'" @click="selectAvailable(index)">全选未分配产品</el-button>
          <el-button size="mini" type="text" :disabled="!route.productIds.length" :aria-label="'清空规则' + (index + 1) + '的产品选择'" @click="updateRoute(index, 'productIds', [])">清空选择</el-button>
        </div>
      </el-form-item>
      <el-form-item label="目标页面" :for="'product-route-page-' + index">
        <el-select :id="'product-route-page-' + index" :value="route.pageId" filterable clearable :aria-label="'规则' + (index + 1) + '目标页面'" placeholder="选择项目内页面" @input="updateRoute(index, 'pageId', $event)">
          <el-option v-if="route.pageId && !pages.some(page => page.id === route.pageId)" :value="route.pageId" label="原目标页面已删除，请重新选择" disabled></el-option>
          <el-option v-for="page in pages" :key="page.id" :value="page.id" :label="page.name + (page.id === main.activePageId ? '（当前页）' : '')"></el-option>
        </el-select>
      </el-form-item>
      <el-button size="mini" type="text" icon="el-icon-delete" :aria-label="'删除产品规则' + (index + 1)" @click="removeRoute(index)">删除规则</el-button>
    </fieldset>
    <el-button ref="add" size="small" icon="el-icon-plus" @click="addRoute">添加产品规则</el-button>
    <el-form-item label="未配置产品" for="product-route-fallback" class="editor-product-routes__fallback">
      <el-select id="product-route-fallback" :value="fallback.type" aria-label="未配置产品动作" @input="setFallback('type', $event)">
        <el-option value="none" label="不跳转"></el-option>
        <el-option value="page" label="跳转默认页面"></el-option>
      </el-select>
    </el-form-item>
    <el-form-item v-if="fallback.type === 'page'" label="默认页面" for="product-route-default-page">
      <el-select id="product-route-default-page" :value="fallback.pageId" filterable clearable aria-label="未配置产品的默认页面" placeholder="选择项目内页面" @input="setFallback('pageId', $event)">
        <el-option v-if="fallback.pageId && !pages.some(page => page.id === fallback.pageId)" :value="fallback.pageId" label="原默认页面已删除，请重新选择" disabled></el-option>
        <el-option v-for="page in pages" :key="page.id" :value="page.id" :label="page.name + (page.id === main.activePageId ? '（当前页）' : '')"></el-option>
      </el-select>
    </el-form-item>
    <p class="editor-navigation-hint">同一产品只能分配到一条规则；更换分组请先从原规则移除。按编号匹配，改名不影响跳转。这里不修改后台产品；配置随大屏保存，右上角预览可体验未保存的修改。</p>
  </div>
</template>
<script>
import { productChoices, usesPlatformData } from '@/components/FleetList/model'
import { fetchPlatformProducts } from '@/components/FleetList/platform'
import { createRequestCancellation, isRequestCanceled } from '@/api/http'
import { productRouteIds } from '@/runtime/page-navigation'

function editableRoute (route) {
  const result = { ...route, productIds: [...productRouteIds(route)] }
  delete result.productId
  return result
}

// Element UI 多选的搜索框与外层不是同一元素，将标签补到可聚焦的原生输入框。
function labelProductSearch (el, binding) {
  const input = el.querySelector('.el-select__input')
  if (input) {
    input.id = binding.value.id
    input.setAttribute('aria-label', binding.value.label)
    input.setAttribute('aria-describedby', binding.value.id.replace('product-route-', 'product-route-help-'))
  }
}

export default {
  inject: ['main'],
  props: { value: { type: Object, required: true }, kind: { type: String, default: 'product' } },
  data: () => ({ products: [], loading: false, loadError: '' }),
  directives: { productRouteLabel: { inserted: labelProductSearch, componentUpdated: labelProductSearch } },
  computed: {
    routes () { return Array.isArray(this.value.routes) ? this.value.routes.map(editableRoute) : [] },
    fallback () { return this.value.fallback || { type: 'none' } },
    pages () { return this.main.pageProject.pages },
    platform () { return usesPlatformData(this.main.activeOption) },
    choices () { return this.platform ? this.products : productChoices(this.kind || 'product', this.main.activeObj.data) },
    sourceKey () { return JSON.stringify([this.main.activeObj.index, this.platform, this.kind]) }
  },
  watch: { sourceKey: { immediate: true, handler () { this.loadProducts() } } },
  beforeDestroy () { this._disposed = true; if (this._request) this._request.cancel() },
  methods: {
    updateRoute (index, key, value) {
      const routes = this.routes.map((route, at) => at === index ? { ...editableRoute(route), [key]: Array.isArray(value) ? [...value] : value } : editableRoute(route))
      this.$emit('input', { ...this.value, routes })
    },
    missingProducts (route) { return route.productIds.filter(id => !this.choices.some(item => item.id === id)) },
    productDisabled (id, index) {
      const contains = route => route.productIds.some(value => typeof value === 'string' && value.trim() === id)
      // 已选项仍可取消，包括导入配置中重复的产品，不能把用户锁在错误状态。
      return !contains(this.routes[index]) && this.routes.some((route, at) => at !== index && contains(route))
    },
    selectAvailable (index) {
      const ids = [...this.routes[index].productIds]
      this.choices.forEach(item => {
        if (!this.productDisabled(item.id, index) && !ids.some(id => typeof id === 'string' && id.trim() === item.id)) ids.push(item.id)
      })
      this.updateRoute(index, 'productIds', ids)
    },
    addRoute () {
      const index = this.routes.length
      this.$emit('input', { ...this.value, routes: [...this.routes.map(editableRoute), { productIds: [], pageId: '' }] })
      this.focusProduct(index)
    },
    removeRoute (index) {
      const next = Math.min(index, this.routes.length - 2)
      this.$emit('input', { ...this.value, routes: this.routes.filter((route, at) => at !== index).map(editableRoute) })
      this.focusProduct(next)
    },
    focusProduct (index) {
      this.$nextTick(() => {
        const ref = this.$refs['product-' + index] || this.$refs.add
        const control = Array.isArray(ref) ? ref[0] : ref
        if (control && control.focus) control.focus()
        else if (control && control.$el) control.$el.focus()
      })
    },
    setFallback (key, value) { this.$emit('input', { ...this.value, fallback: { ...this.fallback, [key]: value } }) },
    async loadProducts () {
      const generation = this._generation = (this._generation || 0) + 1
      if (this._request) this._request.cancel()
      this.products = []; this.loadError = ''; this.loading = false
      if (!this.platform) return
      this._request = createRequestCancellation(); this.loading = true
      try {
        const products = await fetchPlatformProducts({ cancelToken: this._request.token })
        if (!this._disposed && generation === this._generation) this.products = products
      } catch (error) {
        if (!this._disposed && generation === this._generation && !isRequestCanceled(error)) this.loadError = error.message || '产品读取失败'
      } finally {
        if (!this._disposed && generation === this._generation) this.loading = false
      }
    }
  }
}
</script>
<style scoped>
.editor-product-routes { min-width: 0; }
.editor-product-routes .el-select { width: 100%; min-width: 0; }
.editor-product-routes__rule { min-width: 0; margin: 12px 0; padding: 12px 0 0; border: 0; border-top: 1px solid var(--editor-edge, #255267); }
.editor-product-routes__rule legend { padding-right: 8px; font-size: 12px; color: var(--editor-text, #d8f4ff); }
.editor-product-routes__fallback { margin-top: 18px; }
.editor-product-routes__selection-actions { display: flex; flex-wrap: wrap; gap: 8px 16px; }
.editor-product-routes__selection-actions .el-button + .el-button { margin-left: 0; }
@media (pointer: coarse) { .editor-product-routes .el-button { min-height: 44px; } }
</style>
